import Link from 'next/link'
import type { CSSProperties } from 'react'
import type { Industry } from '@/types/exam'
import type { ExamAccent } from '@/data/free-resources/types'

export interface DeckSummary {
  slug: string
  examSlug: string
  name: string
  fullName: string
  industry: Industry
  org: string
  cardCount: number
  imageCount: number
  topTopic: string
  /** Unique topic labels across the deck — powers search. */
  topics: string[]
  accent: ExamAccent
}

/** A small stack of flashcards rendered in CSS, tinted to the exam accent.
 *  Image decks get an ECG trace; every deck shows its own top topic. */
function DeckIllustration({ deck }: { deck: DeckSummary }) {
  return (
    <div className="ps-zoomable relative w-[82%] max-w-62.5">
      <div className="border-border/60 bg-bg absolute inset-0 translate-x-3 translate-y-3 rotate-[3deg] rounded-[14px] border" />
      <div className="border-border/80 bg-bg absolute inset-0 translate-x-1.5 translate-y-1.5 rotate-[1.4deg] rounded-[14px] border" />
      <div className="border-border bg-bg relative flex flex-col gap-2.5 rounded-[14px] border px-4.5 py-4 shadow-[0_10px_26px_-10px_rgba(17,24,39,0.22)]">
        <span className="self-start rounded-full bg-(--accent-tint) px-2.25 py-0.75 text-[10px] font-bold tracking-wide text-(--accent-text) uppercase">
          {deck.topTopic}
        </span>
        {deck.imageCount > 0 ? (
          <svg
            viewBox="0 0 140 26"
            aria-hidden
            className="h-6 w-full text-(--accent)"
            preserveAspectRatio="none"
          >
            <path
              d="M0 15 H16 L20 13 L23 15 H34 L38 5 L41 22 L44 15 H62 L66 13 L69 15 H80 L84 5 L87 22 L90 15 H108 L112 13 L115 15 H140"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
              className="[animation:psFill_2.6s_ease-in-out_infinite_alternate]"
            />
          </svg>
        ) : (
          <>
            <span className="bg-border h-1.75 w-[92%] rounded" />
            <span className="h-1.75 w-3/5 [animation:psFill_2.8s_ease-in-out_infinite_alternate] rounded bg-(--accent)" />
          </>
        )}
        <div className="mt-0.5 flex items-center justify-between">
          <span className="text-hint font-mono text-[8px] font-bold tracking-wide">
            TAP TO FLIP
          </span>
          <span className="flex gap-1">
            <span className="bg-border size-1.5 rounded-full" />
            <span className="size-1.5 rounded-full bg-(--accent)" />
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Premium split deck card for the flashcard directory — the flashcard sibling
 * of PdfCard/LinkCardIllustrated. Copy + FREE pill + CTA left, an accent-tinted
 * CSS flashcard stack right that zooms on hover. Domain-labelled; each card
 * carries the exam's own accent and top topic so no two read the same.
 */
export function FlashcardDeckCard({ deck }: { deck: DeckSummary }) {
  const blurb =
    deck.imageCount > 0
      ? `${deck.cardCount} cards on the topics that decide exam day, ${deck.imageCount} with real strips and waveforms.`
      : `${deck.cardCount} cards on the topics ${deck.name} candidates drill hardest, graded Anki-style.`

  return (
    <Link
      href={`/free-flashcards/${deck.slug}`}
      style={
        {
          '--accent': deck.accent.base,
          '--accent-text': deck.accent.text,
          '--accent-tint': deck.accent.tint,
          '--accent-border': deck.accent.border,
        } as CSSProperties
      }
      className="ps-card ps-split bg-bg border-border hover:border-border-hover overflow-hidden rounded-[20px] border no-underline transition-[border-color,box-shadow] duration-180 hover:shadow-[0_10px_28px_rgba(17,24,39,0.06)]"
    >
      <div className="flex flex-col justify-center gap-2 p-6 md:pl-7">
        <p className="flex items-center gap-2 text-[11.5px] font-bold tracking-[0.09em] uppercase">
          <span className="text-(--accent-text)">{deck.name}</span>
          <span aria-hidden className="bg-border h-2.5 w-px" />
          <span className="text-hint">{deck.industry}</span>
        </p>
        <h3 className="text-ink text-[19px] font-bold tracking-[-0.015em]">
          Free {deck.name} Flashcard Practice
        </h3>
        <p className="text-body text-sm leading-[1.55]">{blurb}</p>
        <p className="mt-1.5 flex items-center gap-3">
          <span className="bg-badge-cream text-warning rounded-full px-3 py-1.25 text-xs font-bold">
            FREE
          </span>
          <span className="text-sm font-bold text-(--accent-text)">
            Start flipping →
          </span>
        </p>
      </div>
      <div className="ps-media bg-mist flex aspect-square items-center justify-center p-6.5">
        <DeckIllustration deck={deck} />
      </div>
    </Link>
  )
}
